import { useCallback, useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, ShieldCheck, ShieldAlert } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchProcessors,
  createProcessor,
  updateProcessor,
  deleteProcessor,
  DP_STR as S,
  type DataProcessor,
  type ProcessorBody,
} from './dataProcessorsApi'

const EMPTY: ProcessorBody = { name: '', purpose: '', data_categories: '', location: '', contact: '', dpa_signed: false, status: 'active' }

export function DataProcessorsScreen() {
  const { toast } = useToast()
  const [status, setStatus] = useState('')
  const [rows, setRows] = useState<DataProcessor[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [editId, setEditId] = useState<number | null>(null) // null=none, 0=new
  const [f, setF] = useState<ProcessorBody>(EMPTY)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchProcessors(status, ac.signal).then(setRows).catch((e) => {
      if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
    })
    return () => ac.abort()
  }, [tick, status])

  const save = async () => {
    if (!f.name.trim()) return
    setBusy(true)
    try {
      if (editId && editId > 0) await updateProcessor(editId, f)
      else await createProcessor(f)
      setEditId(null)
      setF(EMPTY)
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const del = async (id: number) => {
    try {
      await deleteProcessor(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }
  const openEdit = (p: DataProcessor) => {
    setEditId(p.id)
    setF({ name: p.name, purpose: p.purpose, data_categories: p.data_categories, location: p.location, contact: p.contact, dpa_signed: p.dpa_signed, status: p.status })
  }

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A19"
        description={S.desc}
        actions={
          <button type="button" onClick={() => { setEditId(0); setF(EMPTY) }} className="flex items-center gap-1 rounded-md bg-accent px-3 py-1 text-sm text-white">
            <Plus size={14} />
            {S.create}
          </button>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {editId !== null && (
            <div className="space-y-2 rounded-lg border border-accent bg-surface-2 p-3">
              <div className="flex flex-wrap gap-2">
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={S.name} className="min-w-[140px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
                <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder={S.location} className="w-36 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
              </div>
              <input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} placeholder={S.purpose} className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
              <input value={f.data_categories} onChange={(e) => setF({ ...f, data_categories: e.target.value })} placeholder={S.categories} className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
              <div className="flex flex-wrap items-center gap-3">
                <input value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} placeholder={S.contact} className="w-48 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
                <label className="flex items-center gap-1 text-sm text-secondary">
                  <input type="checkbox" checked={f.dpa_signed} onChange={(e) => setF({ ...f, dpa_signed: e.target.checked })} />
                  {S.dpa}
                </label>
                <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary">
                  <option value="active">{S.active}</option>
                  <option value="terminated">{S.terminated}</option>
                </select>
                <button type="button" onClick={save} disabled={busy || !f.name.trim()} className="ml-auto rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50">{S.save}</button>
                <button type="button" onClick={() => setEditId(null)} className="rounded-md border border-border-default px-4 py-2 text-sm text-secondary">{S.cancel}</button>
              </div>
            </div>
          )}

          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-sm text-secondary">
            <option value="">{S.all}</option>
            <option value="active">{S.active}</option>
            <option value="terminated">{S.terminated}</option>
          </select>

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 5 }).map((_, i) => (<Skeleton key={i} className="h-16 w-full" />))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <div className="space-y-2">
              {rows.map((p) => (
                <div key={p.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-primary">{p.name}</span>
                        {p.dpa_signed ? (
                          <span className="flex items-center gap-1 text-xs text-success"><ShieldCheck size={12} />DPA</span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs text-warning"><ShieldAlert size={12} />{S.noDpa}</span>
                        )}
                        {p.status === 'terminated' && <span className="rounded bg-surface-3 px-1.5 py-0.5 text-xs text-tertiary">{S.terminated}</span>}
                      </div>
                      {p.purpose && <p className="mt-1 text-sm text-secondary">{p.purpose}</p>}
                      <p className="text-xs text-tertiary">{[p.data_categories, p.location, p.contact].filter(Boolean).join(' · ')}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button type="button" onClick={() => openEdit(p)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-primary" title={S.edit}><Pencil size={15} /></button>
                      <button type="button" onClick={() => del(p.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={S.delete}><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
